import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const TOKEN_KEY = "ghostinbox_token";
const ADMIN_TOKEN_KEY = "ghostinbox_admin_token";
const IMPERSONATING_KEY = "ghostinbox_impersonating";

const AuthContext = createContext(null);

async function authFetch(path, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(path, { ...options, headers });
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text };
    }
  }

  return { ok: response.ok, status: response.status, data };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [impersonating, setImpersonating] = useState(
    () => sessionStorage.getItem(IMPERSONATING_KEY) === "1",
  );

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATING_KEY);
    setUser(null);
    setImpersonating(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        if (!cancelled) setReady(true);
        return;
      }

      const { ok, status, data } = await authFetch("/api/auth/me");
      if (cancelled) return;

      if (ok && data.user) {
        setUser(data.user);
        setImpersonating(sessionStorage.getItem(IMPERSONATING_KEY) === "1");
      } else {
        clearSession();
        if (status === 403 && data.code === "BLOCKED") {
          sessionStorage.setItem("ghostinbox_blocked", "1");
        }
      }
      setReady(true);
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const signUp = async ({ name, email, password, acceptedTerms, captchaToken, captchaAnswer }) => {
    const { ok, data } = await authFetch("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify({
        name,
        email,
        password,
        acceptedTerms,
        captchaToken,
        captchaAnswer,
      }),
    });

    if (!ok) {
      return { ok: false, error: data.error || "Sign up failed." };
    }

    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return { ok: true };
  };

  const login = async ({ email, password }) => {
    const { ok, status, data } = await authFetch("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    if (!ok) {
      return {
        ok: false,
        error: data.error || "Login failed.",
        blocked: status === 403 && data.code === "BLOCKED",
      };
    }

    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATING_KEY);
    setImpersonating(false);
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return { ok: true, user: data.user };
  };

  const logout = () => {
    clearSession();
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const saveActiveInbox = async (inboxName) => {
    const normalized = inboxName?.trim().toLowerCase();
    if (!normalized) return;

    const { ok, data } = await authFetch("/api/auth/inbox", {
      method: "PATCH",
      body: JSON.stringify({ inboxName: normalized }),
    });

    if (ok && data.user) {
      setUser(data.user);
    }
  };

  const updateProfile = async ({ name }) => {
    const { ok, data } = await authFetch("/api/auth/profile", {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });

    if (!ok) {
      return { ok: false, error: data.error || "Failed to update profile." };
    }

    if (data.user) {
      setUser(data.user);
    }
    return { ok: true, user: data.user };
  };

  const launchAsUser = async (userId) => {
    const { ok, status, data } = await authFetch(`/api/admin/launch/${userId}`, {
      method: "POST",
    });

    if (!ok) {
      if (status === 404 && !data.error) {
        return {
          ok: false,
          error: "Launch API not found. Stop the backend and run `node index.js` again.",
        };
      }
      return { ok: false, error: data.error || "Failed to launch account." };
    }

    const adminToken = localStorage.getItem(TOKEN_KEY);
    if (adminToken) {
      sessionStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
    }
    sessionStorage.setItem(IMPERSONATING_KEY, "1");
    setImpersonating(true);
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return { ok: true, user: data.user };
  };

  const exitImpersonation = async () => {
    const adminToken = sessionStorage.getItem(ADMIN_TOKEN_KEY);
    if (!adminToken) {
      clearSession();
      return { ok: false };
    }

    sessionStorage.removeItem(IMPERSONATING_KEY);
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setImpersonating(false);
    localStorage.setItem(TOKEN_KEY, adminToken);

    const { ok, data } = await authFetch("/api/auth/me");
    if (ok && data.user) {
      setUser(data.user);
      return { ok: true, user: data.user };
    }

    clearSession();
    return { ok: false };
  };

  const syncUser = useCallback((userData) => {
    if (userData) setUser(userData);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        ready,
        signUp,
        login,
        logout,
        getAuthHeaders,
        saveActiveInbox,
        updateProfile,
        syncUser,
        launchAsUser,
        exitImpersonation,
        impersonating,
        isAuthenticated: !!user,
        isAdmin: user?.role === "admin" && !impersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
